/**
 * Parsing des GetCapabilities WMS du SMC.
 *
 * Le serveur retourne un XML; on l'analyse pour extraire :
 *   - dateo : date/heure d'origine de la prévision (Date)
 *   - datef : date/heure de fin disponible (Date)
 *   - deltaHours : pas de temps en heures (number)
 */

const WMS_BASE_URL = 'https://geo.weather.gc.ca/geomet?SERVICE=WMS&VERSION=1.3.0';

/**
 * Télécharge et analyse les capabilities WMS pour une couche donnée.
 *
 * @param {string} layerName - ex: 'HRDPS.CONTINENTAL_TT'
 * @param {AbortSignal} [signal]
 * @returns {Promise<{dateo: Date, datef: Date, deltaHours: number}>}
 */
async function fetchWithRetry(url, fetchOpts, maxRetries = 3) {
  let delay = 2000;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const resp = await fetch(url, fetchOpts);
    if (resp.status !== 429 || attempt === maxRetries) return resp;
    await new Promise(resolve => setTimeout(resolve, delay));
    delay *= 2;
  }
}

export async function fetchCapabilities(layerName, signal) {
  const url = `${WMS_BASE_URL}&REQUEST=GetCapabilities&LAYERS=${layerName}`;
  //console.log(url)
  const resp = await fetchWithRetry(url, signal ? { signal } : {});
  if (!resp.ok) throw new Error(`GetCapabilities HTTP ${resp.status} pour ${layerName}`);
  const text = await resp.text();
  return parseCapabilities(text);
}

/**
 * Extrait dateo, datef et deltaHours depuis le XML GetCapabilities du SMC.
 *
 * Format de la dimension temporelle dans le XML :
 *   "2023-05-13T00:00:00Z/2023-05-23T00:00:00Z/PT3H"
 *    ^^^^^^^^^^^^^^^^^^^^  ^^^^^^^^^^^^^^^^^^^^  ^^^^
 *    dateo (20 chars)       datef (20 chars)      intervalle
 *
 * @param {string} xmlText - texte XML brut
 * @returns {{dateo: Date, datef: Date, deltaHours: number}}
 * @throws {Error} si la dimension temporelle est introuvable ou mal formée
 */
export function parseCapabilities(xmlText) {
  const re = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)\/(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)\/(PT?\d+H)/g;
  let best = null;
  let m;
  while ((m = re.exec(xmlText)) !== null) {
    const dateo = new Date(m[1]);
    const datef = new Date(m[2]);
    const deltaHours = parseInterval(m[3]);
    // Garder la plage avec le dateo le plus récent (= passe courante)
    if (!best || dateo > best.dateo) {
      best = { dateo, datef, deltaHours };
    }
  }
  if (!best) {
    throw new Error('Dimension temporelle introuvable dans les capabilities WMS');
  }
  return best;
}

/**
 * Convertit un intervalle ISO 8601 simplifié en heures.
 * Supporte : PT1H, PT3H, PT6H, PT12H, PT24H, P1D
 *
 * @param {string} interval - ex: 'PT3H', 'P1D'
 * @returns {number} heures
 * @throws {Error} si le format n'est pas reconnu
 */
export function parseInterval(interval) {
  let m;
  // PTnH → n heures
  m = interval.match(/^PT(\d+)H$/);
  if (m) return parseInt(m[1], 10);

  // PnD → n * 24 heures
  m = interval.match(/^P(\d+)D$/);
  if (m) return parseInt(m[1], 10) * 24;

  throw new Error(`Format d'intervalle non supporté : "${interval}"`);
}
